<template>
  <div id="app" class="min-h-screen bg-gray-50 font-sans text-slate-900 flex flex-col overflow-x-hidden w-full relative">
    
    <header class="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          
          <div class="flex items-center shrink-0">
            <router-link to="/" class="flex items-center group">
              <svg class="h-8 w-8 text-indigo-600 transition-colors group-hover:text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15l6-6 4 4 8-8" />
              </svg>
              <span class="ml-2 text-xl font-bold text-gray-900 tracking-tight group-hover:text-black">SinceInception</span>
            </router-link>
          </div>

          <nav v-if="isWizardMode" class="hidden md:flex items-center space-x-1" aria-label="Progress">
            <template v-for="(step, index) in steps" :key="step.name">
              <div 
                class="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors"
                :class="getStepClass(index)"
              >
                <svg v-if="index < currentStepIndex" class="w-4 h-4 mr-2 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span v-else class="w-5 h-5 flex items-center justify-center rounded-full text-xs mr-2 border" :class="index === currentStepIndex ? 'border-indigo-600 text-indigo-600 font-bold' : 'border-gray-300 text-gray-400'">
                  {{ index + 1 }}
                </span>
                {{ step.label }}
              </div>
              <svg v-if="index < steps.length - 1" class="w-4 h-4 text-gray-300 mx-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </template>
          </nav>

          <nav v-else class="hidden md:flex items-center gap-1">
             <router-link 
               to="/benchmarks" 
               class="px-4 py-2 rounded-full text-sm font-medium transition-all"
               active-class="bg-gray-100 text-gray-900 font-bold"
               class-active="active"
             >
               Benchmarks
             </router-link>
             
             <router-link 
               to="/pro" 
               class="px-4 py-2 rounded-full text-sm font-medium text-gray-500 hover:text-gray-900 transition-all"
               active-class="bg-gray-100 text-gray-900 font-bold"
             >
               Talk to a Pro
             </router-link>

             <div class="h-4 w-px bg-gray-300 mx-2"></div>
             
             <router-link to="/select-risk" class="ml-2 text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors">
               Check Your Portfolio &rarr;
             </router-link>
          </nav>

          <div class="md:hidden flex items-center gap-4">
            <span v-if="isWizardMode" class="text-sm font-medium text-gray-500">
              Step <span class="text-indigo-600 font-bold">{{ currentStepIndex + 1 }}</span>/{{ steps.length }}
            </span>
             <router-link v-else to="/benchmarks" class="text-sm font-medium text-gray-900">
               Benchmarks
             </router-link>
          </div>

        </div>
      </div>
    </header>

    <main class="flex-grow w-full">
      <router-view />
    </main>

    <footer class="bg-white border-t border-gray-200 py-10 mt-auto">
      <div class="max-w-5xl mx-auto px-6 text-center space-y-6">
        <p class="text-xs text-gray-400 leading-relaxed max-w-4xl mx-auto">
          <strong>Not Investment Advice:</strong> SinceInception is an educational tool. We are not a registered investment advisor. Returns do not reflect trading fees or taxes.
        </p>
        <div class="pt-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>&copy; {{ new Date().getFullYear() }} SinceInception.</p>
          <div class="flex space-x-6">
            <router-link to="/benchmarks" class="hover:text-gray-900">Benchmarks</router-link>
            <router-link to="/pro" class="hover:text-gray-900">Contact</router-link>
          </div>
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const wizardRouteNames = ['RiskSelection', 'PortfolioForm', 'PerformanceCompare', 'ResultsNextSteps'];

const isWizardMode = computed(() => {
  return wizardRouteNames.includes(route.name);
});

const steps = [
  { name: 'RiskSelection', label: 'Benchmark' },
  { name: 'PortfolioForm', label: 'Inputs' },
  { name: 'PerformanceCompare', label: 'Results' },
  { name: 'ResultsNextSteps', label: 'Plan' },
];

const currentStepIndex = computed(() => {
  return steps.findIndex(s => s.name === route.name);
});

function getStepClass(index) {
  const current = currentStepIndex.value;
  if (index < current) return 'text-gray-900';
  if (index === current) return 'text-indigo-600 bg-indigo-50';
  return 'text-gray-400';
}
</script>

<style>
@import "@/assets/main.css";
</style>